import { usePersonnel } from 'features/Responder/features/Personnel';
import { useTours } from 'features/Responder/features/Tours';
import { deriveCheckpointsFromTour } from 'features/Responder/features/Tours/utils';
import React, { useMemo } from 'react';
import { Badge, Button, Card, Form, Stack } from 'react-bootstrap';
import { useResponderMap } from 'features/Responder/features/ResponderMap/contexts/ResponderMapContext';

const formatCount = (tour) => {
  const count = deriveCheckpointsFromTour(tour)?.checkpoints?.length || 0;
  return count ? `${count} checkpoint${count === 1 ? '' : 's'}` : 'No checkpoints';
};

export default function TourSelectorPanel() {
  const { tours } = useTours();
  const { selectedTourIds, setSelectedTourIds, toggleTourSelection } = useResponderMap();
  const { personnel } = usePersonnel();

  const activeTourIds = useMemo(() => {
    const ids = new Set();
    const pullId = (val) => {
      if (val === null || val === undefined) return null;
      const num = Number(val);
      return Number.isFinite(num) ? num : `${val}`;
    };
    // Try tour id first, then route id as fallback.
    tours?.forEach?.((tour) => {
      const statusEntries = tour?.personnelStatus || {};
      Object.values(statusEntries).forEach((ps) => {
        const activeId = pullId(ps?.activeTourId ?? ps?.currentTourId ?? ps?.tourId ?? ps?.routeId);
        if (activeId != null && `${activeId}` === `${tour.id}`) {
          ids.add(`${tour.id}`);
        }
      });
    });

    // try live personnel data as well
    Object.values(personnel || {}).forEach((person) => {
      const candidates = [
        person?.activeTourId,
        person?.currentTourId,
        person?.currentTourId,
        person?.currentRouteId,
        person?.activeTourId,
        person?.tour?.id,
      ]
        .map(pullId)
        .filter((v) => v !== null);
      candidates.forEach((id) => ids.add(`${id}`));
    });
    return ids;
  }, [tours, personnel]);

  const selectedTourKeys = useMemo(
    () => new Set(selectedTourIds.map((id) => `${id}`)),
    [selectedTourIds],
  );

  const allChecked = useMemo(() => {
    if (!Array.isArray(tours) || !tours.length) return false;
    return tours.every((tour) => selectedTourKeys.has(`${tour.id}`));
  }, [selectedTourKeys, tours]);

  const toggleAll = () => {
    if (allChecked) {
      setSelectedTourIds([]);
    } else {
      const allIds = (tours || []).map((gt) => gt.id).filter((id) => id != null);
      setSelectedTourIds(allIds);
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <Card.Title className="mb-0">Tours on Map</Card.Title>
            <Card.Text className="text-muted small mb-0">
              Select which tours render on the shared map.
            </Card.Text>
          </div>
          <Button size="sm" variant="outline-primary" onClick={toggleAll} disabled={!tours?.length}>
            {allChecked ? 'Hide all' : 'Show all'}
          </Button>
        </div>
        {Array.isArray(tours) && tours.length ? (
          <Stack gap={2}>
            {tours.map((tour) => {
              const isChecked = selectedTourKeys.has(`${tour.id}`);
              const isActive = activeTourIds.has(`${tour.id}`);
              return (
                <div
                  key={tour.id || tour.name}
                  className={`d-flex justify-content-between align-items-start p-2 rounded ${isChecked ? 'bg-primary bg-opacity-10 border border-primary' : 'border border-secondary-subtle'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    toggleTourSelection(tour.id);
                  }}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        toggleTourSelection(tour.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div>
                      <div className="fw-semibold">{tour.name || 'Untitled tour'}</div>
                      <div className="small text-muted">{formatCount(tour)}</div>
                    </div>
                  </div>
                  <Badge bg={isActive ? 'success' : 'secondary'}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              );
            })}
          </Stack>
        ) : (
          <div className="text-muted small">No tours available.</div>
        )}
      </Card.Body>
    </Card>
  );
}
